import React, { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import axios from 'axios';
import './AccountDashboard.css';

function AccountDashboard() {
  const navigate = useNavigate();
  const [user, setUser] = useState(null);
  const [orders, setOrders] = useState([]);
  const [wishlistCount, setWishlistCount] = useState(0);
  const [addressCount, setAddressCount] = useState(0);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    // Check if user is logged in
    const token = localStorage.getItem('authToken');
    const userData = localStorage.getItem('user');

    if (!token || !userData) {
      navigate('/login');
      return;
    }

    const parsedUser = JSON.parse(userData);

    // Check if user is a customer
    if (parsedUser.role !== 'customer') {
      navigate('/admin/dashboard');
      return;
    }

    setUser(parsedUser);
    fetchRecentOrders(parsedUser.email);
    fetchWishlistCount();
    fetchAddressCount();
  }, [navigate]);

  const fetchRecentOrders = async (email) => {
    try {
      const response = await axios.get(`/api/customers/${email}/orders`);
      // Get only the 3 most recent orders
      setOrders(response.data.slice(0, 3));
    } catch (error) {
      console.error('Error fetching orders:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchWishlistCount = async () => {
    try {
      const token = localStorage.getItem('authToken');
      const response = await axios.get('/api/auth/wishlist', {
        headers: { Authorization: `Bearer ${token}` }
      });
      setWishlistCount(response.data.length);
    } catch (error) {
      console.error('Error fetching wishlist count:', error);
    }
  };

  const fetchAddressCount = async () => {
    try {
      const token = localStorage.getItem('authToken');
      const response = await axios.get('/api/auth/addresses', {
        headers: { Authorization: `Bearer ${token}` }
      });
      setAddressCount(response.data.length);
    } catch (error) {
      console.error('Error fetching address count:', error);
    }
  };

  const handleLogout = () => {
    localStorage.removeItem('authToken');
    localStorage.removeItem('user');
    navigate('/');
  };

  if (loading) {
    return (
      <div className="account-dashboard">
        <div className="loading">Loading...</div>
      </div>
    );
  }

  return (
    <div className="account-dashboard">
      <div className="account-container">
        <div className="account-sidebar">
          <div className="account-user-info">
            <div className="user-avatar">
              {user?.name ? user.name.charAt(0).toUpperCase() : 'U'}
            </div>
            <h3>{user?.name || 'Customer'}</h3>
            <p>{user?.email}</p>
          </div>

          <nav className="account-nav">
            <Link to="/account" className="account-nav-item active">
              <span className="nav-icon">📊</span>
              Dashboard
            </Link>
            <Link to="/account/orders" className="account-nav-item">
              <span className="nav-icon">📦</span>
              Order History
            </Link>
            <Link to="/account/addresses" className="account-nav-item">
              <span className="nav-icon">📍</span>
              Addresses
            </Link>
            <Link to="/account/wishlist" className="account-nav-item">
              <span className="nav-icon">❤️</span>
              Wishlist
            </Link>
            <Link to="/account/settings" className="account-nav-item">
              <span className="nav-icon">⚙️</span>
              Settings
            </Link>
            <button onClick={handleLogout} className="account-nav-item logout-btn">
              <span className="nav-icon">🚪</span>
              Logout
            </button>
          </nav>
        </div>

        <div className="account-main">
          <div className="account-header">
            <h1>My Account</h1>
            <p>Welcome back, {user?.name || 'Customer'}!</p>
          </div>

          <div className="account-stats">
            <div className="stat-card">
              <div className="stat-icon">📦</div>
              <div className="stat-content">
                <h3>{orders.length}</h3>
                <p>Recent Orders</p>
              </div>
            </div>

            <div className="stat-card">
              <div className="stat-icon">❤️</div>
              <div className="stat-content">
                <h3>{wishlistCount}</h3>
                <p>Wishlist Items</p>
              </div>
            </div>

            <div className="stat-card">
              <div className="stat-icon">📍</div>
              <div className="stat-content">
                <h3>{addressCount}</h3>
                <p>Saved Addresses</p>
              </div>
            </div>
          </div>

          <div className="account-section">
            <div className="section-header">
              <h2>Recent Orders</h2>
              <Link to="/account/orders" className="btn-link">View All</Link>
            </div>

            {orders.length === 0 ? (
              <div className="empty-state">
                <p>You haven't placed any orders yet.</p>
                <Link to="/" className="btn btn-primary">Start Shopping</Link>
              </div>
            ) : (
              <div className="orders-list">
                {orders.map(order => (
                  <div key={order.id} className="order-card">
                    <div className="order-header">
                      <div>
                        <h3>Order #{order.order_number}</h3>
                        <p className="order-date">
                          {new Date(order.created_at).toLocaleDateString('en-US', {
                            year: 'numeric',
                            month: 'long',
                            day: 'numeric'
                          })}
                        </p>
                      </div>
                      <div className={`order-status status-${order.status}`}>
                        {order.status}
                      </div>
                    </div>
                    <div className="order-details">
                      <p>{order.item_count} item{order.item_count !== 1 ? 's' : ''}</p>
                      <p className="order-total">${parseFloat(order.total).toFixed(2)}</p>
                    </div>
                    <Link to={`/account/orders/${order.id}`} className="btn btn-secondary-outline btn-sm">
                      View Details
                    </Link>
                  </div>
                ))}
              </div>
            )}
          </div>

          <div className="account-section">
            <div className="section-header">
              <h2>Quick Actions</h2>
            </div>
            <div className="quick-actions">
              <Link to="/account/orders" className="action-card">
                <span className="action-icon">📦</span>
                <h3>Track Orders</h3>
                <p>View and track your orders</p>
              </Link>
              <Link to="/account/addresses" className="action-card">
                <span className="action-icon">📍</span>
                <h3>Manage Addresses</h3>
                <p>Update shipping addresses</p>
              </Link>
              <Link to="/account/settings" className="action-card">
                <span className="action-icon">⚙️</span>
                <h3>Account Settings</h3>
                <p>Update your profile</p>
              </Link>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

export default AccountDashboard;
